import { Box } from '@material-ui/core';
import { LenderApprovalStatus, LenderName } from '@nemuru-eng/js-api-client';
import React from 'react';

import { LoanApplicationMode } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/types/LoanApplicationMode';
import { getLoanApplicationStatusTagProps } from '@/components/shared/components/ConditionsSummary/LoanConditionsSummary/components/LenderApplications/utils/getTagProps';
import { ContrastedCard } from '@/components/shared/components/ContrastedCard';
import { LoanApplicationErrorIcon } from '@/components/shared/components/ErrorIcon/components/LoanApplicationErrorIcon';
import { Font } from '@/components/shared/components/Font';
import { LenderLogo } from '@/components/shared/components/Logo/LenderLogo/LenderLogo';
import { StatusTag } from '@/components/shared/components/StatusTag/StatusTag';
import { Text } from '@/components/shared/components/Text';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface LoanApplicationCardProps {
  onClick: () => void;
  lenderName: LenderName;
  isSelected: boolean;
  lenderApprovalStatus?: LenderApprovalStatus | null;
  merchantChannelAgentViewMode?: boolean;
  mode: LoanApplicationMode;
}

export const LoanApplicationCard = ({
  onClick,
  lenderName,
  isSelected,
  lenderApprovalStatus,
  merchantChannelAgentViewMode = false,
  mode,
}: LoanApplicationCardProps) => {
  const { palette } = useCustomTheme();

  const borderStyle = isSelected
    ? {
        outline: '2px solid',
        outlineOffset: '-1px',
        outlineColor: palette.secondary.main,
        transition: 'all 0.1s ease',
      }
    : { outline: '0px solid #ffffff00', outlineOffset: '-1px' };

  const unselectedLogoStyle = {
    opacity: 0.5,
    filter: 'grayscale(100%) brightness(65%) contrast(150%)',
  } as React.CSSProperties;

  const InnerComponent = (
    <Box
      display={'flex'}
      alignItems={'center'}
      justifyContent={'space-between'}
      width={'100%'}
      mx={1}
      my={-1}
      className={'appear-anim'}
    >
      <>
        <Box style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          {lenderName && <LenderLogo lenderName={lenderName} style={isSelected ? {} : unselectedLogoStyle} />}
        </Box>
        <Box>
          {lenderApprovalStatus ? (
            <StatusTag {...getLoanApplicationStatusTagProps(lenderApprovalStatus)} />
          ) : (
            <Font variant={'caption'}>
              {merchantChannelAgentViewMode ? (
                <>
                  <Text tId={'PROCESSING_WITH_DOTS'} />
                </>
              ) : (
                <Box display={'flex'} alignItems={'center'}>
                  {isSelected && (
                    <Box mr={3}>
                      <LoanApplicationErrorIcon />
                    </Box>
                  )}
                  <Text tId={'NO_RESPONSE'} />
                </Box>
              )}{' '}
            </Font>
          )}
        </Box>
      </>
    </Box>
  );

  if (mode !== LoanApplicationMode.VIEW_ONLY) {
    return (
      <ContrastedCard
        onClick={onClick}
        boxProps={{
          style: { minHeight: '68px', boxSizing: 'border-box', justifyContent: 'center' },
        }}
        paperProps={{ style: { ...borderStyle } }}
        endIcon={'launch'}
      >
        {InnerComponent}
      </ContrastedCard>
    );
  }

  return (
    <ContrastedCard
      boxProps={{
        style: { minHeight: '68px', boxSizing: 'border-box', justifyContent: 'center' },
      }}
      paperProps={{ style: { ...borderStyle } }}
    >
      {InnerComponent}
    </ContrastedCard>
  );
};
